/**
 * Modal for inviting forensic accountants to collaborate on a report
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { useFocusTrap } from '../hooks/useFocusTrap';
import axios from '@/lib/http';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface InviteAccountantModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  onInviteSent: () => void;
}

export default function InviteAccountantModal({ 
  isOpen, 
  onClose, 
  reportId,
  onInviteSent 
}: InviteAccountantModalProps) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [permissions, setPermissions] = useState<string[]>(['view', 'comment', 'edit', 'sign']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/collaboration/invites`,
        {
          report_id: reportId,
          email,
          message,
          permissions
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      setInviteLink(`${window.location.origin}/accountant/accept-invite/${response.data.invite_token}`);
      
      // Log invite activity
      logActivity({
        reportId,
        actionType: 'invite',
        actionDetails: `Invited accountant ${email} to collaborate`,
        section: 'collaboration',
        metadata: { invited_email: email, permissions }
      });
      
      onInviteSent();
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setEmail('');
        setMessage('');
        setSuccess(false);
        setInviteLink('');
        onClose();
      }, 3000);
      
    } catch (err: any) {
      console.error('Failed to send invite:', err);
      setError(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (perm: string) => {
    if (permissions.includes(perm)) {
      setPermissions(permissions.filter(p => p !== perm));
    } else {
      setPermissions([...permissions, perm]);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Invite Forensic Accountant" className="rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="p-6 rounded-t-2xl" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <h2 className="text-xl font-bold">Invite Forensic Accountant</h2>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-white hover:bg-opacity-20 rounded-lg p-1 transition-colors"
              style={{ color: 'var(--color-on-brand)' }}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'transparent' }}>
                <svg className="w-8 h-8" style={{ color: 'var(--color-verified)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Invite Sent!</h3>
              <p className="mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                An invitation has been sent to <span className="font-semibold">{email}</span>
              </p>

              {inviteLink && (
                <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>Share this link:</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                    />
                    <button
                      onClick={copyInviteLink}
                      className="px-4 py-2 rounded-lg transition-colors text-sm"
                      style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="border px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Accountant Email Address <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)' }}
                  placeholder="accountant@forensicfirm.com"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  The accountant will receive access to this report only
                </p>
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                  Permissions
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'view', label: 'View Report', desc: 'Can view report details and calculations' },
                    { value: 'comment', label: 'Add Comments', desc: 'Can add comments and notes' },
                    { value: 'edit', label: 'Edit Report', desc: 'Can modify report data (with version history)' },
                    { value: 'sign', label: 'E-Signature', desc: 'Can provide electronic signature' }
                  ].map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-start p-3 border rounded-lg cursor-pointer transition-colors"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <input
                        type="checkbox"
                        checked={permissions.includes(perm.value)}
                        onChange={() => togglePermission(perm.value)}
                        className="mt-1 h-4 w-4 rounded"
                        style={{ accentColor: 'var(--color-brand)', borderColor: 'var(--color-border)' }}
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{perm.label}</div>
                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{perm.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Optional Message */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Message (Optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent resize-none"
                  style={{ borderColor: 'var(--color-border)' }}
                  placeholder="Add a personal message for the accountant..."
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border rounded-lg transition-colors font-medium"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || permissions.length === 0}
                  className="flex-1 px-4 py-3 rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
